// Entry point.
//
// The pre-migration entry point published `window.dashboard` so a second, activity-first view
// stayed reachable from the console -- it was the only entrance, because both view buttons
// shipped `hidden`. That view and its console entrance are both gone: it was deleted rather
// than migrated, along with its endpoints and its Snowflake queries. See the README.

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { ApiError } from './api/client';
import { AuthProvider } from './auth/AuthContext';
import { App } from './App';
import { ChatProvider } from './chat/ChatProvider';
import './styles/index.css';

// The payload behind this page rebuilds twice a day, just behind each Fivetran sync, so nothing
// here benefits from aggressive refetching. It does benefit from *some*, which `staleTime:
// Infinity` ruled out: GRO-80 removed the Refresh button on the grounds that opening the page
// fetches current data, and that is only true of a tab somebody opened recently. A dashboard
// left open across a sync boundary was silently a day behind with no control to fix it and no
// hint that it needed one. Five minutes is well under the twelve-hour cadence, so coming back to
// the tab does what the button used to and a page being read continuously still refetches at
// most twice an hour.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
      // A 401 or 403 is an answer, not a hiccup. Retrying either wastes a round trip and
      // delays the gate the user actually needs to see. A scoped refusal arrives as an
      // `ApiError` rather than an `AccessDeniedError`, so the status is checked too -- nothing
      // the server said was wrong with the *request* gets better by asking again.
      retry: (failureCount, error) => {
        const name = (error as Error).name;
        if (name === 'SessionExpiredError' || name === 'AccessDeniedError') return false;
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          {/* Above the routes, so a Scout turn outlives the page that started it (GRO-274). */}
          <ChatProvider>
            <App />
          </ChatProvider>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
